// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useEffect, useState } from "react";
import { useKeycloak } from "../../context/KeycloakProvider";
import { useTheme } from "../../context/ThemeContext";
import LoadingScreen from "../LoadingScreen";
import env from "../../utils/env";

const UserSyncHandler = ({ children }) => {
  const {
    authenticated,
    userInfo,
    syncedUser,
    syncLoading,
    syncUserWithDatabase,
    getUserInfo,
  } = useKeycloak();

  const { isDarkMode } = useTheme();
  const [hasAttemptedSync, setHasAttemptedSync] = useState(false);
  const [syncFailed, setSyncFailed] = useState(false);
  const [showLoadingUI, setShowLoadingUI] = useState(false);

  useEffect(() => {
    let currentUserInfo = userInfo;
    if (!currentUserInfo && authenticated) {
      currentUserInfo = getUserInfo();
    }

    // Don't attempt sync if it already failed or if user has no meaningful roles
    if (authenticated && currentUserInfo && !syncLoading && !syncFailed) {
      const resourceName = env.REACT_APP_KEYCLOAK_CLIENT_ID;
      const keycloakRoles =
        currentUserInfo?.resourceRoles?.[resourceName]?.roles || [];
      const meaningfulRoles = keycloakRoles.filter(
        (role) =>
          role !== "none" &&
          role !== "offline_access" &&
          role !== "default-roles-vhp" &&
          role !== "uma_authorization",
      );

      // Only sync if user has meaningful roles
      if (meaningfulRoles.length > 0 && (!hasAttemptedSync || !syncedUser)) {
        setHasAttemptedSync(true);

        // Only show loading UI on first sync (not on refresh if cached)
        const loadingTimer = setTimeout(() => {
          // Only show loading if sync takes more than 500ms
          if (!syncedUser) {
            setShowLoadingUI(true);
          }
        }, 500);

        syncUserWithDatabase(currentUserInfo)
          .then(() => {
            setSyncFailed(false);
            setShowLoadingUI(false);
            clearTimeout(loadingTimer);
          })
          .catch((error) => {
            console.error("User sync failed:", error);
            clearTimeout(loadingTimer);
            setShowLoadingUI(false);

            // 403 means the user lacks permission to sync — don't retry this session
            if (error.response?.status === 403) {
              setSyncFailed(true);
            } else {
              setHasAttemptedSync(false);
            }
          });
      } else if (meaningfulRoles.length === 0) {
        // User has no meaningful roles, mark as no sync needed
        setHasAttemptedSync(true);
        setSyncFailed(false);
      }
    }
  }, [
    authenticated,
    userInfo,
    syncedUser,
    syncLoading,
    syncUserWithDatabase,
    hasAttemptedSync,
    getUserInfo,
    syncFailed,
  ]);

  useEffect(() => {
    if (!authenticated) {
      setHasAttemptedSync(false);
      setSyncFailed(false);
      setShowLoadingUI(false);
    }
  }, [authenticated]);

  // Only show loading if:
  // 1. User is authenticated
  // 2. User is not synced yet
  // 3. Currently syncing
  // 4. Sync takes more than 500ms (showLoadingUI is true)
  if (authenticated && !syncedUser && syncLoading && showLoadingUI) {
    return <LoadingScreen isDarkMode={isDarkMode} />;
  }

  // If user is authenticated but sync failed, still show the app
  // Permissions will handle access control
  return children;
};

export default UserSyncHandler;
